import { useEffect, useState } from "react";

import { useActivationGaMappingDetailQuery } from "app/pages/activation/components/ga-connect";

import type { IGaSectionContentProps } from "./GaSection";
import type { GaMappingSettings } from "./types";

interface IUseGaSectionHandlersParams {
    activationId: string;
    onChange: () => void;
}

export const useGaSectionHandlers = ({ activationId, onChange }: IUseGaSectionHandlersParams) => {
    const [gaMappingSettings, setGaMappingSettings] = useState<GaMappingSettings>(null);

    const {
        data,
        isLoading,
        isError: isQueryError,
    } = useActivationGaMappingDetailQuery(activationId, {
        shouldIgnoreResourceNotFoundError: true,
    });

    useEffect(() => {
        if (!data || data?.state === "Faulty") {
            setGaMappingSettings(null);
            return;
        }

        setGaMappingSettings({
            connectionId: data.connectionId,
            isActive: data.state === "Active",
            isChanged: false,
            propertyId: data.propertyId,
            propertyName: data.propertyName,
            streamId: data.streamId,
        });
    }, [data]);

    const onPropertySelected: IGaSectionContentProps["onPropertySelected"] = async ({
        connectionId,
        propertyId,
        propertyName,
        streamId,
    }) =>
        new Promise((res) => {
            if (gaMappingSettings?.propertyId !== propertyId || gaMappingSettings?.streamId !== streamId) {
                setGaMappingSettings((previousSettings) => ({
                    ...previousSettings,
                    connectionId,
                    isActive: true,
                    isChanged: true,
                    propertyId,
                    propertyName,
                    streamId,
                }));
                onChange();
            }

            res();
        });

    const onStreamChange: IGaSectionContentProps["onStreamChange"] = (streamId) => {
        if (gaMappingSettings.streamId === streamId) {
            return;
        }

        setGaMappingSettings((previousSettings) => ({
            ...previousSettings,
            isActive: true,
            isChanged: true,
            streamId,
        }));
        onChange();
    };

    const onSwitchClick: IGaSectionContentProps["onSwitchClick"] = () => {
        setGaMappingSettings((previousSettings) => ({
            ...previousSettings,
            isActive: !previousSettings.isActive,
            isChanged: true,
        }));
        onChange();
    };

    return {
        gaMapping: gaMappingSettings,
        isQueryError,
        isLoading,
        isMappingError: data?.state === "Faulty",
        onPropertySelected,
        onStreamChange,
        onSwitchClick,
    };
};
